/**
 * 광고 차단 감지 서비스
 * - 여러 방법으로 광고 차단기 감지
 * - 결과를 캐싱하여 반복 체크 최소화
 * 
 * 주의: 애드센스가 완전히 승인된 후에만 정확하게 동작
 */

type AdBlockStatus = 'unknown' | 'detected' | 'not_detected';

// 애드센스 승인 완료 후 true로 변경
const ADSENSE_APPROVED = false;

class AdBlockDetector {
  private status: AdBlockStatus = 'unknown';
  private checkPromise: Promise<boolean> | null = null;

  /**
   * 광고 차단 여부 확인
   * @returns true = 광고 차단 감지됨
   */
  async isBlocked(): Promise<boolean> {
    // 애드센스 미승인 상태면 항상 false 반환 (차단 감지 비활성화)
    if (!ADSENSE_APPROVED) {
      console.log('[AdBlockDetector] AdSense not approved yet, skipping detection');
      return false;
    }
    
    // 이미 체크 중이면 기존 Promise 반환
    if (this.checkPromise) {
      return this.checkPromise;
    }

    // 캐시된 결과 있으면 반환
    if (this.status !== 'unknown') {
      return this.status === 'detected';
    }

    this.checkPromise = this.detectAdBlock();
    const result = await this.checkPromise;
    this.checkPromise = null;
    
    return result;
  }

  /**
   * 캐시 초기화 (재검사 필요시)
   */
  reset() {
    this.status = 'unknown';
    this.checkPromise = null;
  }

  private async detectAdBlock(): Promise<boolean> {
    const checks = await Promise.all([
      this.checkBaitElement(),
      this.checkGoogleAds(),
      this.checkAdsByGoogle(),
    ]);

    // 하나라도 차단 감지되면 true
    const isBlocked = checks.some(result => result);
    this.status = isBlocked ? 'detected' : 'not_detected';
    
    console.log('[AdBlockDetector] Detection results:', {
      baitElement: checks[0],
      googleAds: checks[1],
      adsByGoogle: checks[2],
      final: isBlocked
    });

    return isBlocked;
  }

  /**
   * 방법 1: 미끼 요소 생성 후 숨겨졌는지 확인
   */
  private async checkBaitElement(): Promise<boolean> {
    return new Promise((resolve) => {
      const bait = document.createElement('div');
      bait.className = 'adsbox ad-banner textads banner-ads';
      bait.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
        width: 1px;
        height: 1px;
      `;
      bait.innerHTML = '&nbsp;';
      document.body.appendChild(bait);

      // 약간의 딜레이 후 체크 (광고 차단기가 DOM 수정할 시간)
      setTimeout(() => {
        const isBlocked = 
          bait.offsetHeight === 0 || 
          bait.offsetWidth === 0 ||
          bait.clientHeight === 0 ||
          getComputedStyle(bait).display === 'none' ||
          getComputedStyle(bait).visibility === 'hidden';
        
        document.body.removeChild(bait);
        resolve(isBlocked);
      }, 100);
    });
  }

  /**
   * 방법 2: Google Ads 스크립트 로드 확인
   */
  private async checkGoogleAds(): Promise<boolean> {
    return new Promise((resolve) => {
      // window.adsbygoogle 존재 여부 확인
      if (typeof window !== 'undefined') {
        // 이미 로드된 경우
        if ((window as unknown as { adsbygoogle?: unknown[] }).adsbygoogle !== undefined) {
          resolve(false);
          return;
        }
      }

      // 광고 스크립트 로드 시도
      const testScript = document.createElement('script');
      testScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
      testScript.async = true;
      
      let resolved = false;

      testScript.onload = () => {
        if (!resolved) {
          resolved = true;
          resolve(false); // 로드 성공 = 차단 안됨
        }
      };

      testScript.onerror = () => {
        if (!resolved) {
          resolved = true;
          resolve(true); // 로드 실패 = 차단됨
        }
      };

      // 타임아웃
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve(true); // 타임아웃 = 차단 추정
        }
      }, 3000);

      // 이미 스크립트가 있으면 중복 추가 안함
      const existingScript = document.querySelector('script[src*="adsbygoogle"]');
      if (!existingScript) {
        document.head.appendChild(testScript);
      } else {
        resolve(false);
      }
    });
  }

  /**
   * 방법 3: adsbygoogle 배열 push 동작 확인
   */
  private async checkAdsByGoogle(): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const win = window as unknown as { adsbygoogle?: unknown[] };
        
        if (typeof win.adsbygoogle === 'undefined') {
          resolve(true); // 배열 없음 = 차단
          return;
        }

        // 배열에 push 시도
        try {
          win.adsbygoogle.push({});
          
          // push가 성공했으면 통과
          setTimeout(() => {
            resolve(false);
          }, 100);
        } catch {
          resolve(true);
        }
      }, 500);
    });
  }
}

export const adBlockDetector = new AdBlockDetector();
